/**
 * ClaimReadyHero — agentowy hook „Twoja strona już czeka" (styl Von Halsky)
 * dla nieprzejętych stron hodowli (`/hodowla/:slug`) i firm (`/firma/:slug`).
 *
 * Komunikuje: „przygotowaliśmy Ci gotową stronę — przejmij ją za darmo,
 * a resztę uzupełnisz w panelu w każdej chwili".
 *
 * Wspólny dla obu typów (parytet hodowca ↔ biznes). Modale claim/report/remove
 * pozostają w KennelComplianceNotice / BusinessComplianceNotice.
 */
import React from 'react';
import { UserCheck, PencilLine, Trash2, Check, Sparkles } from 'lucide-react';

const CONTENT = {
  kennel: {
    headline: 'Znaleźliśmy Twoją hodowlę i przygotowaliśmy gotową stronę',
    claimLabel: 'Przejmij stronę za darmo',
    removeLabel: 'Usuń stronę',
    benefits: [
      'Zarządzaj psami, miotami i zapytaniami',
      'Strona w katalogu ApisAI i widoczna w Google',
      'Odbieraj wiadomości bezpośrednio od klientów',
    ],
    disclaimerNoun: 'tej hodowli',
  },
  business: {
    headline: 'Znaleźliśmy Twoją firmę i przygotowaliśmy gotową wizytówkę',
    claimLabel: 'Przejmij wizytówkę za darmo',
    removeLabel: 'Usuń wizytówkę',
    benefits: [
      'Zarządzaj usługami, portfolio i zapytaniami',
      'Wizytówka w katalogu ApisAI i widoczna w Google',
      'Odbieraj wiadomości bezpośrednio od klientów',
    ],
    disclaimerNoun: 'tej firmy',
  },
};

export default function ClaimReadyHero({
  kind = 'business',
  entityName,
  onClaim,
  onReport,
  onRemove,
  accent = '#F97316',
  mascotSrc = '/mascot-user-dog.png', // maskotka pieska APIS; ustaw null, by pokazać ikonę
  testids = {},
}) {
  const c = CONTENT[kind] || CONTENT.business;
  const name = (entityName && String(entityName).trim()) || (kind === 'kennel' ? 'Twoja hodowla' : 'Twoja firma');

  return (
    <section
      data-testid={testids.root || 'claim-ready-hero'}
      style={{
        position: 'relative', zIndex: 60,
        background: 'linear-gradient(135deg, #00273D 0%, #01151F 100%)',
        borderBottom: `2px solid ${accent}`,
        color: '#e6eef3',
        padding: '26px 24px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '24px' }}>
        {/* Awatar asystenta APIS (maskotkę pieska można wstawić przez prop mascotSrc) */}
        <div style={{ flexShrink: 0 }}>
          <div style={{
            width: '84px', height: '84px', borderRadius: '999px',
            background: 'rgba(255,255,255,0.06)', border: `2px solid ${accent}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 28px rgba(249,115,22,0.28)', overflow: 'hidden',
          }}>
            {mascotSrc ? (
              <img src={mascotSrc} alt="Asystent APIS" width="84" height="84"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Sparkles style={{ width: '38px', height: '38px', color: accent }} />
            )}
          </div>
        </div>

        {/* Tekst + korzyści */}
        <div style={{ flex: 1, minWidth: '280px' }}>
          <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: accent, fontWeight: 700, marginBottom: '6px' }}>
            Asystent APIS
          </div>
          <h2 data-testid="claim-hero-headline" style={{ fontSize: '20px', lineHeight: 1.3, fontWeight: 700, color: '#fff', margin: '0 0 6px' }}>
            {c.headline}
          </h2>
          <p style={{ fontSize: '13.5px', lineHeight: 1.55, color: '#9fb3c0', margin: '0 0 12px' }}>
            <strong style={{ color: '#cbd8e1' }}>„{name}"</strong> to dopiero początek — wszystko uzupełnisz i zmienisz w panelu w każdej chwili.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
            {c.benefits.map((b) => (
              <li key={b} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: '#cbd8e1' }}>
                <Check style={{ width: '15px', height: '15px', color: accent, flexShrink: 0 }} /> {b}
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '10px', minWidth: '220px' }}>
          <button
            data-testid={testids.claim || 'claim-hero-claim-btn'}
            onClick={onClaim}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              background: accent, color: '#fff', border: 'none',
              padding: '13px 22px', borderRadius: '999px', fontSize: '14.5px', fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 8px 24px rgba(249,115,22,0.35)',
              transition: 'transform 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <UserCheck style={{ width: '17px', height: '17px' }} /> {c.claimLabel}
          </button>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button data-testid={testids.report || 'claim-hero-report-btn'} onClick={onReport}
              style={{ background: 'transparent', border: 'none', color: '#9fb3c0', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: 0 }}>
              <PencilLine style={{ width: '13px', height: '13px' }} /> Popraw dane
            </button>
            <button data-testid={testids.remove || 'claim-hero-remove-btn'} onClick={onRemove}
              style={{ background: 'transparent', border: 'none', color: '#c98a8a', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: 0 }}>
              <Trash2 style={{ width: '13px', height: '13px' }} /> {c.removeLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Dyskleimer RODO (drugorzędny, wymagany prawnie dla danych publicznych) */}
      <div style={{ maxWidth: '1100px', margin: '14px auto 0', fontSize: '11px', lineHeight: 1.5, color: '#6f8592' }}>
        To nieoficjalna strona utworzona na podstawie publicznie dostępnych danych. ApisAI nie jest właścicielem {c.disclaimerNoun} ani jej przedstawicielem.{' '}
        <a href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" style={{ color: '#8aa2b0', textDecoration: 'underline' }}>Polityka prywatności</a>.
      </div>
    </section>
  );
}
